import React, { Suspense, useRef, useState } from "react"
import Camera from "@mui/icons-material/CameraAltTwoTone"
import CameraTwoToneIcon from "@mui/icons-material/CameraTwoTone"
import { Dialog, DialogContent } from "@mui/material"
import { useSearchParams } from "react-router-dom"

import { PopupService } from "react-ui-components/components/PopupService"
import { StatButton } from "react-ui-components/components/StatButton"

import { useNamespacedTranslation } from "../hooks/useNamespacedTranslation"
import { useUserStore } from "../components/stores/userStore"
import { applyScanRouteToSearchParams, getScanPayloadRoute } from "../utils/scannedCode"

const QRCamera = React.lazy(() => import("../components/QRCamera"))

export const QrScannerLauncher: React.FC = () => {
  const [isScanning, setIsScanning] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const scanLockRef = useRef(false)
  const { isLoggedIn } = useUserStore()
  const { t } = useNamespacedTranslation(["qrCamera", "common", "app"])

  const closeScanner = () => {
    scanLockRef.current = false
    setIsScanning(false)
  }

  const handleScanSuccess = (decodedText: string) => {
    const scanRoute = getScanPayloadRoute(decodedText)

    if (scanRoute.type === "empty") {
      scanLockRef.current = false
      PopupService.setPopup(t("Sorry there was an error processing this code."))
      return
    }

    const nextParams = applyScanRouteToSearchParams(scanRoute, searchParams)
    setSearchParams(nextParams, { replace: true })
    setIsScanning(false)
  }

  if (!isLoggedIn) {
    return null
  }

  return (
    <>
      <StatButton
        link
        icon={isScanning ? <CameraTwoToneIcon /> : <Camera />}
        label={t("scan")}
        onClick={() => {
          scanLockRef.current = false
          setIsScanning(true)
        }}
      />
      <Dialog
        title=""
        className="flex justify-center"
        open={isScanning}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            closeScanner()
          }
        }}
        disableEscapeKeyDown
        disableRestoreFocus
        keepMounted={false}
      >
        <DialogContent sx={{ padding: 0, margin: 0, position: "relative" }}>
          {isScanning ? (
            <Suspense fallback={null}>
              <QRCamera
                fullScreen
                overlayMessage={t("Center Code")}
                showOverlay
                onScan={(result: any) => {
                  if (scanLockRef.current) {
                    return
                  }
                  scanLockRef.current = true
                  handleScanSuccess(String(result ?? ""))
                }}
                onError={() => {
                  // QRCamera renders its own localized error state.
                }}
                onClose={closeScanner}
              />
            </Suspense>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  )
}

export default QrScannerLauncher
